// 家用面板 P5（docs/panel 05-ui-ux V-05，TP5.5）：插播設定 + 每週統計。只限主人。
//   - 選攝影機（快速設定已預勾室外 / 門口）、類別（預設「人」）、安靜時段、冷卻（預設 5 分鐘）、出不出聲（預設不出聲）
//   - 勾了插播的攝影機下面一個「🔒 要 PIN 才能看 / 🔓 面板上直接看」開關（John 2026-10-21，不再露出室內 / 室外 / 門口）；
//     底層照舊 PUT /v3/devices/{id}/location-tag（所有面板共用，雲端依位置算 live_requires_unlock）；
//     從沒標過的攝影機第一次勾插播時當場問一次「這台拍的是家裡面嗎？」
//   - 近 7 天插播統計（GET interrupt-stats）：總數 + 5 秒內關掉次數、每台次數；
//     5 秒內關掉 ≥ 50%（≥ 10 次）→ 建議取消那台［取消這台］［不用］（不用 = 本機記住這週不再提示；SD §6 第 5 點）
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceName, type DeviceSummary } from './deviceModel';
import {
  dismissSuggest, getInterruptStats, getPanelSettings, suggestDismissed, INTERRUPT_COOLDOWNS, INTERRUPT_KINDS, interruptBody, cameraUntagged, interruptCamerasOf,
  parseInterruptSettings, putPanelSettings, setCameraLocation, statsByCamera,
  type PanelInterruptSettings, type PanelInterruptStats,
} from './panels';
import { CameraPinAsk, CameraPinToggle, type CameraLoc } from './PanelCameraPin';

type Loc = CameraLoc;
const asLoc = (v: string | undefined): Loc => (v === 'indoor' || v === 'outdoor' || v === 'door' ? v : '');

export function PanelInterruptCard({ panelId, devices, onDevicesChanged }: {
  panelId: string;
  devices: DeviceSummary[];
  onDevicesChanged?: () => void;
}) {
  const { t } = useTranslation();
  const [s, setS] = useState<PanelInterruptSettings | null>(null);
  const [stats, setStats] = useState<PanelInterruptStats | null>(null);
  const [locs, setLocs] = useState<Record<string, Loc>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hideSuggest, setHideSuggest] = useState(false);
  const [asking, setAsking] = useState<string | null>(null);
  const cams = useMemo(() => interruptCamerasOf(devices), [devices]);
  const nameOf = useMemo(() => new Map(devices.map((d) => [d.device_id, deviceName(d)])), [devices]);

  const load = useCallback(() => {
    getPanelSettings(panelId).then((r) => setS(parseInterruptSettings(r))).catch(() => setMsg(t('panels.errGeneric')));
    getInterruptStats(panelId).then(setStats).catch(() => setStats(null));
  }, [panelId, t]);
  useEffect(load, [load]);
  useEffect(() => setLocs(Object.fromEntries(cams.map((d) => [d.device_id, asLoc(d.camera_location)]))), [cams]);

  if (!s) return <div className="card"><h2>{t('panels.intr.title')}</h2>{msg && <p className="error-text">{msg}</p>}</div>;
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      let locChanged = false;
      for (const d of cams) {
        const want = locs[d.device_id] ?? '';
        if (want !== asLoc(d.camera_location)) {
          await setCameraLocation(d.device_id, want);
          locChanged = true;
        }
      }
      setS(parseInterruptSettings(await putPanelSettings(panelId, interruptBody(s))));
      if (locChanged) onDevicesChanged?.();
      setMsg(t('panels.disp.saved'));
    } catch {
      setMsg(t('panels.errGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const rows = stats ? statsByCamera(stats) : [];
  const sg = stats?.suggest ?? null;
  // 已經不在插播清單、或這週按過［不用］→ 不再提示
  const suggestion = sg && !hideSuggest && s.cameras.includes(sg.camera_id) && !suggestDismissed(panelId, sg.camera_id) ? sg : null;

  /** ［取消這台］：插播清單拿掉這台（沿用存檔路徑；只改 cameras） */
  const cancelCamera = async (cameraId: string) => {
    setBusy(true);
    setMsg(null);
    try {
      const next = { ...s, cameras: s.cameras.filter((x) => x !== cameraId) };
      setS(parseInterruptSettings(await putPanelSettings(panelId, interruptBody(next))));
      setHideSuggest(true);
      setMsg(t('panels.intr.suggestCancelled'));
    } catch {
      setMsg(t('panels.errGeneric'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card" data-testid="panel-interrupt">
      <h2>{t('panels.intr.title')}</h2>

      <h3>{t('panels.intr.cameras')}</h3>
      {cams.length === 0 && <p className="text-secondary">{t('panels.intr.noCamera')}</p>}
      {cams.map((d) => {
        const loc = locs[d.device_id] ?? '';
        const on = s.cameras.includes(d.device_id);
        return (
          <div key={d.device_id} className="dv-member-row" data-testid={`intr-cam-${d.device_id}`}>
            <label className="dv-check">
              <input type="checkbox" checked={on} onChange={() => {
                setS({ ...s, cameras: toggle(s.cameras, d.device_id) });
                if (!on && cameraUntagged(loc)) setAsking(d.device_id);   // 第一次放進面板、從沒標過 → 當場問一次
              }} />
              {nameOf.get(d.device_id) ?? d.device_id}
            </label>
            {on && <CameraPinToggle deviceId={d.device_id} location={loc} onChange={(l) => setLocs((m) => ({ ...m, [d.device_id]: l }))} />}
          </div>
        );
      })}
      {asking && (
        <CameraPinAsk name={nameOf.get(asking) ?? asking}
          onAnswer={(l) => { setLocs((m) => ({ ...m, [asking]: l })); setAsking(null); }} onDismiss={() => setAsking(null)} />
      )}

      <h3>{t('panels.intr.kinds')}</h3>
      <div className="dv-pairing-actions">
        {INTERRUPT_KINDS.map((k) => (
          <button key={k} className={`evt-chip${s.kinds.includes(k) ? ' active' : ''}`} aria-pressed={s.kinds.includes(k)} onClick={() => setS({ ...s, kinds: toggle(s.kinds, k) })}>
            {t(`panels.intr.kind_${k}`)}
          </button>
        ))}
      </div>

      <label className="dv-check">
        <input type="checkbox" checked={s.includeUnverified} onChange={() => setS({ ...s, includeUnverified: !s.includeUnverified })} />
        {t('panels.intr.unverified')}
      </label>
      <p className="dv-detail-sub">{t('panels.intr.unverifiedHint')}</p>

      <h3>{t('panels.intr.quiet')}</h3>
      <label className="dv-check">
        <input type="checkbox" checked={s.quiet.on} onChange={() => setS({ ...s, quiet: { ...s.quiet, on: !s.quiet.on } })} />
        {t('panels.intr.quietOn')}
      </label>
      {s.quiet.on && (
        <div className="dv-pairing-actions">
          <input type="time" aria-label={t('panels.intr.from')} value={s.quiet.from} onChange={(e) => setS({ ...s, quiet: { ...s.quiet, from: e.target.value } })} />
          <span>–</span>
          <input type="time" aria-label={t('panels.intr.to')} value={s.quiet.to} onChange={(e) => setS({ ...s, quiet: { ...s.quiet, to: e.target.value } })} />
        </div>
      )}

      <label className="dv-pairing-input">
        <span>{t('panels.intr.cooldown')}</span>
        <select value={s.cooldown_s} onChange={(e) => setS({ ...s, cooldown_s: Number(e.target.value) })}>
          {INTERRUPT_COOLDOWNS.map((v) => (
            <option key={v} value={v}>{t('panels.intr.minutes', { n: v / 60 })}</option>
          ))}
        </select>
      </label>
      <label className="dv-check">
        <input type="checkbox" checked={s.sound} onChange={() => setS({ ...s, sound: !s.sound })} />
        {t('panels.intr.sound')}
      </label>

      <div className="dv-pairing-actions">
        <button className="btn-primary" onClick={() => void save()} disabled={busy}>{t('panels.disp.save')}</button>
      </div>
      {msg && <p className="text-secondary">{msg}</p>}

      <h3>{t('panels.intr.stats')}</h3>
      {stats && (
        <div data-testid="panel-interrupt-stats">
          <p>{stats.by_camera.length > 0
            ? t('panels.intr.statsSummary', { n: stats.total, m: stats.closed_lt5s })
            : t('panels.intr.statsTotal', { n: stats.total })}</p>
          {suggestion && (
            <div className="card" data-testid="panel-interrupt-suggest">
              <p>{t('panels.intr.suggest', { name: suggestion.name || nameOf.get(suggestion.camera_id) || suggestion.camera_id })}</p>
              <div className="dv-pairing-actions">
                <button className="btn-primary" onClick={() => void cancelCamera(suggestion.camera_id)} disabled={busy}>{t('panels.intr.suggestCancel')}</button>
                <button onClick={() => { dismissSuggest(panelId, suggestion.camera_id); setHideSuggest(true); }}>{t('panels.intr.suggestKeep')}</button>
              </div>
            </div>
          )}
          {stats.by_camera.length > 0 && stats.by_camera.map((r) => (
            <p key={r.camera_id} className="text-secondary">
              {t('panels.intr.statsCameraClose', { name: r.name || nameOf.get(r.camera_id) || r.camera_id, n: r.shown, m: r.closed_lt5s })}
            </p>
          ))}
          {stats.by_camera.length === 0 && rows.map((r) => (
            <p key={r.camera_id} className="text-secondary">
              {t('panels.intr.statsCamera', {
                name: nameOf.get(r.camera_id) ?? r.camera_id,
                n: r.total,
                kinds: Object.entries(r.kinds).map(([k, n]) => `${t(`panels.intr.kind_${k}`, { defaultValue: k })} ${n}`).join('、'),
              })}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
